// pages/Apps.jsx
import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
  Alert,
  Image,
} from "react-bootstrap";
import { AppsAPI } from "../api/apps";
import Topbar from "../components/Topbar";
import { Link } from "react-router-dom";

const ALL_TAG = "all";

function useQueryParams() {
  const [init, setInit] = useState({});
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const q = p.get("q") || "";
    const tag = p.get("tag") || ALL_TAG;
    setInit({ q, tag });
  }, []);
  return init;
}

function Apps() {
  const qp = useQueryParams();

  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState(ALL_TAG);

  const [tags, setTags] = useState([ALL_TAG]);
  const [apps, setApps] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [loadingApps, setLoadingApps] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (qp.q !== undefined) setQuery(qp.q);
    if (qp.tag !== undefined) setActiveTag(qp.tag);
  }, [qp.q, qp.tag]);

  useEffect(() => {
    let cancelled = false;
    async function loadTags() {
      try {
        setLoadingTags(true);
        setError("");
        const data = await AppsAPI.getTags();
        const list = Array.isArray(data) ? data : [];
        if (!cancelled) setTags([ALL_TAG, ...list]);
      } catch (e) {
        if (!cancelled) {
          setTags([ALL_TAG]);
          setError("Could not load filters. Showing default.");
        }
      } finally {
        if (!cancelled) setLoadingTags(false);
      }
    }
    loadTags();
    return () => { cancelled = true; };
  }, []);

  const [debouncedQ, setDebouncedQ] = useState(query);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    async function loadApps() {
      try {
        setLoadingApps(true);
        setError("");
        const params = new URLSearchParams();
        if (debouncedQ) params.set("q", debouncedQ);
        if (activeTag && activeTag !== ALL_TAG) params.set("tag", activeTag);
        const data = await AppsAPI.getApps(params);
        const list = Array.isArray(data) ? data : [];
        if (!cancelled) setApps(list);
      } catch (e) {
        if (!cancelled) {
          setError("Could not load apps. Please try again.");
          setApps([]);
        }
      } finally {
        if (!cancelled) setLoadingApps(false);
      }
    }
    loadApps();
    return () => { cancelled = true; };
  }, [activeTag, debouncedQ]);

  const isEmpty = !loadingApps && apps.length === 0;

  return (
    <div className="wrapper">
      <Topbar />

      <Container className="py-5">
        <Row className="align-items-end g-4 mb-4">
          <Col xs={12} md>
            <h1 className="display-6 fw-bold text-dark mb-2">Discover More Apps</h1>
            <p className="text-secondary mb-0">
              Explore business-ready apps designed to streamline your workflows. Browse by category or search to find the solution that fits your operations.
            </p>
          </Col>
          <Col xs={12} md={5} lg={4}>
            <Form.Label className="small">Search apps</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <span role="img" aria-label="search">🔎</span>
              </InputGroup.Text>
              <Form.Control
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Try 'inventory', 'doctor', 'jobs'..."
              />
              {loadingApps && (
                <InputGroup.Text>
                  <Spinner animation="border" size="sm" />
                </InputGroup.Text>
              )}
            </InputGroup>
          </Col>
        </Row>

        {error && (
          <Alert variant="warning" className="mb-4 py-2">{error}</Alert>
        )}

        <div className="mb-4">
          <ButtonGroup className="flex-wrap gap-2">
            {tags.map((tag) => (
              <Button
                key={tag}
                size="sm"
                variant={activeTag === tag ? "primary" : "outline-secondary"}
                className="rounded-pill text-capitalize shadow-sm flex-grow-0 px-3"
                onClick={() => setActiveTag(tag)}
                disabled={loadingTags}
              >
                {tag}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        {loadingApps ? (
          <div className="d-flex justify-content-center align-items-center p-5">
            <div className="text-center">
              <Spinner animation="border" role="status" className="mb-3" />
              <p>Loading...</p>
            </div>
          </div>
        ) : (
          <>
            {isEmpty ? (
              <div className="text-center py-5">
                <div className="mx-auto bg-light border rounded-4 d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 48, height: 48 }}>🔍</div>
                <h5 className="fw-semibold">No apps found</h5>
                <p className="text-secondary">Try a different keyword or clear filters.</p>
              </div>
            ) : (
              <Row xs={1} sm={2} lg={3} xl={4} className="g-3 g-lg-4">
                {apps.map((app, i) => (
                  <Col key={i}>
                    <Card className="h-100 shadow-sm">
                      <Card.Body className="d-flex flex-column">
                        <div className="d-flex gap-3">
                          <Image src={app.icon} alt={app.name} className="rounded-3 p-1 bg-light border object-fit-cover" width="60" height="60" />
                          <div className="flex-grow-1">
                            <Card.Title as="h3" className="h6 mb-1 fw-semibold text-dark">
                              {app.name}
                            </Card.Title>
                            <Card.Text className="small text-secondary mb-0">{app.desc}</Card.Text>
                          </div>
                        </div>
                        <div className="d-flex align-items-center justify-content-between mt-3 pt-1">
                          <Badge bg="light" text="secondary" className="text-capitalize fw-light">
                            {app.primaryTag || (app.tags?.[0] ?? "")}
                          </Badge>
                          <Link className="text-decoration-none" to={`/app/${app.slug}`}>Open</Link>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}
      </Container>
    </div>
  );
}

export default Apps;